import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Text,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import accessUtils from '../../../../srcLegacy/shared/utils/accessUtils';
import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { useIsOnReviewStep, useMultiplexDetails, useMultiplexListingFlow } from '../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { useSetListingCriteriaMutation } from '../../../hooks/useSetListingCriteriaMutation';
import numberUtils from '../../../utils/numberUtils';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../../srcLegacy/shared/constants/ClickstreamConstants';
import Criterion from '../../../../srcLegacy/shared/model/ListingCriteria/Criterion';

import PetRow from '../../../../srcLegacy/shared/components/WizardModularListing/PetPolicyAndPetFee/components/PetRow';

import {
  emitNumberOfCatsAllowedEvent,
  emitNumberOfDogsAllowedEvent,
  emitPetPolicyNegotiableEvent,
  EVENT_IDS,
} from '../../../analytics/events/screeningCriteria';
import { useAuth } from '../../AuthProvider';
import { useWizardNavigation } from '../../CreateListingPage/WizardNavigationProvider';

import type { SetListingCriteriaMutationVariables } from '../../../hooks/useSetListingCriteriaMutation';
import type { CriterionProps } from '../../../../srcLegacy/shared/model/ListingCriteria/Criterion';

const MIN_PET_COUNT = 0;
const MAX_PET_COUNT = 9;
const PET_COUNT_ERROR = 'Add at least one pet type to continue';

const PetsAllowedFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();
  const formModeContext = useFormMode();
  const { isScreenDesktop } = useScreenSize();
  const navigation = useWizardNavigation();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { data: multiplexData } = useMultiplexDetails({ enabled: !isOnReviewStep });

  const listingCriteria = listingDetails?.listingCriteria?.criteria ?? [];

  /**
   * Deferred: legacy auto-saves the full criteria map on initial load so "Are pets allowed?" is not
   * inferred only from default counts without persisting session state.
   */
  const currentCriteria: Array<CriterionProps> =
    isInMultiplexListingFlow && !isOnReviewStep && multiplexData?.listingCriteria?.criteria
      ? (multiplexData.listingCriteria.criteria as Array<CriterionProps>)
      : listingCriteria;
  const petsAllowedDto = listingDetails?.petsAllowed;
  const isPetsAllowedUnset =
    petsAllowedDto != null &&
    petsAllowedDto.catsDto?.allowed === undefined &&
    petsAllowedDto.smallDogsDto?.allowed === undefined &&
    petsAllowedDto.largeDogsDto?.allowed === undefined;
  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const active = listingDetails?.active ?? false;

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({ isFeed, isMultifamily });
  const triggerObject = formModeContext.mode === 'modal' ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.PET_POLICY_PAGE;
  const triggerLocation = active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;

  const getCriterion = (type: string) => currentCriteria.find((c) => c.type === type);

  const initialCatCount = getCriterion(RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT)?.criterion ?? '0';
  const initialSmallDogCount =
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT)?.criterion ?? '0';
  const initialLargeDogCount =
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT)?.criterion ?? '0';
  const initialPetDeposit = numberUtils.stripTrailingDecimalZeros(
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT)?.criterion,
  );
  const initialPetRent = numberUtils.stripTrailingDecimalZeros(
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_RENT)?.criterion,
  );
  const initialNegotiable =
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT)?.negotiable ||
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT)?.negotiable ||
    getCriterion(RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT)?.negotiable ||
    false;
  const initialRefundable = getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT)?.refundable;

  const initialArePetsAllowed = (() => {
    if (!petsAllowedDto) return null;
    if (isPetsAllowedUnset) return false;
    return (
      petsAllowedDto.catsDto?.allowed || petsAllowedDto.smallDogsDto?.allowed || petsAllowedDto.largeDogsDto?.allowed
    );
  })();

  const [arePetsAllowed, setArePetsAllowed] = useState<boolean | null>(initialArePetsAllowed ?? null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useSetListingCriteriaMutation,
    skipToast: true,
    meta: {
      invalidates: ['users-properties-criteria-get'],
      context: { api: 'rental-manager', details: 'PetsAllowedFormField' },
    },
  });

  const catCountField = useFieldSave({
    fieldName: 'catCount',
    initialValue: initialCatCount,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT,
          criterion: value,
          negotiable: Boolean(negotiableField.value),
          isEnabled: true,
        }),
      ],
    }),
  });

  const smallDogCountField = useFieldSave({
    fieldName: 'smallDogCount',
    initialValue: initialSmallDogCount,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
          criterion: value,
          negotiable: Boolean(negotiableField.value),
          isEnabled: true,
        }),
      ],
    }),
  });

  const largeDogCountField = useFieldSave({
    fieldName: 'largeDogCount',
    initialValue: initialLargeDogCount,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
          criterion: value,
          negotiable: Boolean(negotiableField.value),
          isEnabled: true,
        }),
      ],
    }),
  });

  const petDepositField = useFieldSave({
    fieldName: 'petPolicyPetDeposit',
    initialValue: initialPetDeposit,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
          criterion: value,
          negotiable: Boolean(negotiableField.value),
          refundable: refundableField.value === false ? false : (initialRefundable ?? null),
          isEnabled: true,
        }),
      ],
    }),
  });

  const petRentField = useFieldSave({
    fieldName: 'petPolicyPetRent',
    initialValue: initialPetRent,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT,
          criterion: value,
          negotiable: Boolean(negotiableField.value),
          isEnabled: true,
        }),
      ],
    }),
  });

  const negotiableField = useFieldSave({
    fieldName: 'negotiable',
    initialValue: initialNegotiable,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT,
          criterion: catCountField.value,
          negotiable: Boolean(value),
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
          criterion: smallDogCountField.value,
          negotiable: Boolean(value),
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
          criterion: largeDogCountField.value,
          negotiable: Boolean(value),
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
          criterion: petDepositField.value,
          negotiable: Boolean(value),
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT,
          criterion: petRentField.value,
          negotiable: Boolean(value),
          isEnabled: true,
        }),
      ],
    }),
  });

  const refundableField = useFieldSave({
    fieldName: 'petDepositRefundable',
    initialValue: initialRefundable === false ? false : null,
    mutation: updateMutation,
    autoSaveOnChange: true,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
          criterion: petDepositField.value,
          negotiable: Boolean(negotiableField.value),
          refundable: value === false ? false : null,
          isEnabled: true,
        }),
      ],
    }),
  });

  const isPetCountError =
    arePetsAllowed === true &&
    catCountField.value === '0' &&
    smallDogCountField.value === '0' &&
    largeDogCountField.value === '0';

  useEffect(() => {
    if (isPetCountError) {
      navigation?.reportError();
      return () => navigation?.clearError();
    }
  }, [isPetCountError, navigation]);

  const updateMutationRef = useRef(updateMutation);
  updateMutationRef.current = updateMutation;
  const hasSavedDefaultRef = useRef(false);

  useEffect(() => {
    if (!isPetsAllowedUnset || hasSavedDefaultRef.current) return;
    hasSavedDefaultRef.current = true;
    // Set defaults when unset
    updateMutationRef.current.mutate({
      getParams: { listingAlias },
      postData: [
        Criterion({ type: RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT, criterion: '0', isEnabled: true }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT,
          criterion: '0',
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT,
          criterion: '0',
          isEnabled: true,
        }),
        Criterion({
          type: RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT,
          criterion: '',
          refundable: initialRefundable ?? null,
          isEnabled: true,
        }),
        Criterion({ type: RentalManagerListingCriteriaType.PET_POLICY_PET_RENT, criterion: '', isEnabled: true }),
      ],
    });
  }, [isPetsAllowedUnset]);

  const isDepositDisabled =
    catCountField.value === '0' && smallDogCountField.value === '0' && largeDogCountField.value === '0';

  const handleCountChange = (name: string, delta: number, currentValue: string) => {
    const newValue = Math.min(
      MAX_PET_COUNT,
      Math.max(MIN_PET_COUNT, parseInt(currentValue || '0', 10) + delta),
    ).toString();

    if (name === RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT) {
      catCountField.onChange(newValue);
      if (listingDetails && user) {
        emitNumberOfCatsAllowedEvent({ currentProperty: listingDetails, user, triggerLocation, triggerObject });
      }
    } else if (name === RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT) {
      smallDogCountField.onChange(newValue);
      if (listingDetails && user) {
        emitNumberOfDogsAllowedEvent({ currentProperty: listingDetails, user, triggerLocation, triggerObject });
      }
    } else if (name === RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT) {
      largeDogCountField.onChange(newValue);
      if (listingDetails && user) {
        emitNumberOfDogsAllowedEvent({ currentProperty: listingDetails, user, triggerLocation, triggerObject });
      }
    }
  };

  const resetAllPetCounts = () => {
    catCountField.onChange('0');
    smallDogCountField.onChange('0');
    largeDogCountField.onChange('0');
    petDepositField.onChange('');
    petRentField.onChange('');
  };

  const handleArePetsAllowedChange = (newValue: boolean) => {
    if (newValue === false && arePetsAllowed === true) {
      if (formModeContext.mode === 'modal') {
        setArePetsAllowed(false);
        resetAllPetCounts();
      } else {
        setShowConfirmModal(true);
      }
    } else {
      setArePetsAllowed(newValue);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirmModal(false);
    setArePetsAllowed(false);
    resetAllPetCounts();
  };

  const handleCancelNo = () => {
    setShowConfirmModal(false);
  };

  const shouldConsolidateErrors =
    negotiableField.error != undefined &&
    negotiableField.error === petDepositField.error &&
    negotiableField.error === petRentField.error;

  return (
    <div data-testid="PetsAllowedFormField">
      {shouldConsolidateErrors && (
        <Alert tone="critical" css={{ marginBottom: '400' }}>
          {negotiableField.error}
        </Alert>
      )}
      <FieldSet required={true} legend={<Legend>Are pets allowed?</Legend>} data-testid="PetsAllowedFieldSet">
        <ToggleButtonGroup
          data-testid="PetsAllowedToggleButtonContainer"
          aria-label="Are pets allowed?"
          conjoined={false}
        >
          <ToggleButton
            name="PetsAllowed"
            selected={arePetsAllowed === true}
            onSelectedChange={(selected) => selected && handleArePetsAllowedChange(true)}
            css={{ marginRight: '200' }}
            data-eventid={EVENT_IDS.PET_POLICY_NEGOTIABLE}
          >
            Yes
          </ToggleButton>

          <ToggleButton
            name="PetsAllowed"
            selected={arePetsAllowed === false}
            onSelectedChange={(selected) => selected && handleArePetsAllowedChange(false)}
          >
            No
          </ToggleButton>
        </ToggleButtonGroup>

        {Boolean(arePetsAllowed) && (
          <FormField
            data-testid="PetsAllowedConditionalFormField"
            error={isPetCountError}
            description={isPetCountError ? <FormHelp>{PET_COUNT_ERROR}</FormHelp> : undefined}
            label={
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '400',
                }}
                data-testid="PetsAllowedHeader"
              >
                <Text textStyle="body-sm-bold">Pet type</Text>
                <Text textStyle="body-sm-bold" css={{ marginRight: '600' }}>
                  Maximum
                </Text>
              </Box>
            }
          >
            <PetRow
              label="Cats"
              name={RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT}
              value={catCountField.value}
              onPlusClick={(e) => handleCountChange(e.currentTarget.name, 1, catCountField.value)}
              onMinusClick={(e) => handleCountChange(e.currentTarget.name, -1, catCountField.value)}
            />
            <PetRow
              label="Small dogs (under 40 lb)"
              name={RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT}
              value={smallDogCountField.value}
              onPlusClick={(e) => handleCountChange(e.currentTarget.name, 1, smallDogCountField.value)}
              onMinusClick={(e) => handleCountChange(e.currentTarget.name, -1, smallDogCountField.value)}
            />
            <PetRow
              label="Large dogs (over 40 lb)"
              name={RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT}
              value={largeDogCountField.value}
              onPlusClick={(e) => handleCountChange(e.currentTarget.name, 1, largeDogCountField.value)}
              onMinusClick={(e) => handleCountChange(e.currentTarget.name, -1, largeDogCountField.value)}
            />
          </FormField>
        )}

        {arePetsAllowed === false && (
          <Alert css={{ marginBlock: '400' }}>
            You aren’t required to allow pets, but may still be asked to allow assistance animals
          </Alert>
        )}

        <LabeledControl
          label={<Label>Pet policy is negotiable</Label>}
          disabled={arePetsAllowed === null}
          control={
            <Checkbox
              name="negotiable"
              checked={Boolean(negotiableField.value)}
              onChange={(e) => {
                negotiableField.onChange(e.target.checked);
                if (listingDetails && user) {
                  emitPetPolicyNegotiableEvent({
                    currentProperty: listingDetails,
                    user,
                    triggerLocation,
                    triggerObject,
                  });
                }
              }}
              data-eventid={EVENT_IDS.PET_POLICY_NEGOTIABLE}
              css={{ marginInlineStart: '0' }}
            />
          }
        />
      </FieldSet>

      {arePetsAllowed === true && (
        <FieldSet
          legend={<Legend>Do you charge any pet fees?</Legend>}
          data-testid="PetFeesFieldSet"
          css={{ marginTop: '400' }}
        >
          <FormField
            data-testid="PetDepositFormField"
            label={<Label required={false}>Pet deposit</Label>}
            error={!shouldConsolidateErrors && !!petDepositField.error}
            control={
              <AdornedInput
                size={isScreenDesktop ? 'lg' : 'md'}
                input={
                  <AdornedInput.Input
                    name={RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT}
                    value={petDepositField.value}
                    placeholder="0"
                    onChange={(e) => petDepositField.onChange(e.target.value)}
                    onBlur={petDepositField.onBlur}
                    id="pet-deposit-input"
                    disabled={isDepositDisabled}
                  />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
              />
            }
            description={
              <FormHelp>
                {!shouldConsolidateErrors && petDepositField.error
                  ? petDepositField.error
                  : 'Set to $0 if you will not collect a deposit'}
              </FormHelp>
            }
          />

          {canAccessSFCostsAndFees && (
            <LabeledControl
              label={<Label>This is a one-time, non-refundable fee</Label>}
              control={
                <Checkbox
                  name="petDepositRefundable"
                  checked={refundableField.value === false}
                  onChange={(e) => refundableField.onChange(e.target.checked ? false : null)}
                  disabled={isDepositDisabled}
                />
              }
            />
          )}

          <FormField
            data-testid="PetRentFormField"
            label={<Label required={false}>Pet rent</Label>}
            error={!shouldConsolidateErrors && !!petRentField.error}
            control={
              <AdornedInput
                size={isScreenDesktop ? 'lg' : 'md'}
                input={
                  <AdornedInput.Input
                    name={RentalManagerListingCriteriaType.PET_POLICY_PET_RENT}
                    value={petRentField.value}
                    placeholder="0"
                    onChange={(e) => petRentField.onChange(e.target.value)}
                    onBlur={petRentField.onBlur}
                    id="pet-rent-input"
                    disabled={isDepositDisabled}
                  />
                }
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
              />
            }
            description={
              <FormHelp>
                {!shouldConsolidateErrors && petRentField.error
                  ? petRentField.error
                  : 'Set to $0 if you will not collect pet rent'}
              </FormHelp>
            }
          />
        </FieldSet>
      )}

      <TextButton
        asChild
        textStyle="body-sm"
        css={{ marginRight: '200', marginTop: '400' }}
        icon={<IconExternalOutline />}
        iconPosition="end"
      >
        <a
          href="https://www.hud.gov/program_offices/fair_housing_equal_opp/assistance_animals"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about assistance animal exemptions
        </a>
      </TextButton>

      <Modal
        body={
          <div data-testid="ConfirmNoPetsModalContent">
            <Text textStyle="heading-xs" css={{ marginBottom: '200' }} asChild>
              <p>Change your pet policy settings?</p>
            </Text>
            <Text asChild>
              <p>
                Your previously added pet policy details will not be saved. &quot;Cancel&quot; to go back to editing
                your pet policy, or &quot;Continue&quot; if you&apos;ve decided not to allow pets.
              </p>
            </Text>
          </div>
        }
        footer={
          <ButtonGroup aria-label="Pet policy actions">
            <TextButton onClick={handleCancelNo}>Cancel</TextButton>
            <Button tone="brand" emphasis="filled" onClick={handleConfirmNo}>
              Continue
            </Button>
          </ButtonGroup>
        }
        open={showConfirmModal}
        onOpenChange={(open) => !open && handleCancelNo()}
      />
    </div>
  );
};

export default PetsAllowedFormField;
